import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Clock, CheckCircle2, Circle, Search, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { membersApi } from "@/lib/api/directory";

export default function PendingActivationPage() {
    const { lang } = useI18n();
    const location = useLocation();
    const navigate  = useNavigate();

    // State passed from the signup wizard via navigate()
    const stateRef    = location.state?.payment_reference;
    const stateCode   = location.state?.member_code;

    const [member, setMember]   = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        membersApi.me()
            .then((m) => {
                setMember(m);
                if (m.activation_status === "ACTIVE") {
                    navigate("/member/card", { replace: true });
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [navigate]);

    const payRef  = stateRef  || member?.payment_reference;
    const memCode = stateCode || member?.member_code;

    const checklist = [
        {
            done: true,
            label: lang === "ar" ? "تم إنشاء الحساب" : "Account created",
            sub:   lang === "ar" ? `كود عضويتك: ${memCode || "…"}` : `Your member code: ${memCode || "…"}`,
        },
        {
            done: false,
            label: lang === "ar" ? "التحقق من الدفع" : "Payment verification",
            sub:   payRef
                ? (lang === "ar" ? `المرجع: ${payRef}` : `Reference: ${payRef}`)
                : (lang === "ar" ? "في انتظار مرجع الدفع" : "Awaiting payment reference"),
        },
        {
            done: false,
            label: lang === "ar" ? "تفعيل العضوية" : "Membership activation",
            sub:   lang === "ar" ? "سيتم تفعيل بطاقتك خلال ٢–١٢ ساعة" : "Your card will be activated within 2–12 hours",
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-16">

            {/* Status badge */}
            <div className="flex items-center justify-center mb-8">
                <div className="inline-flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-50 dark:bg-amber-950/20 px-6 py-3">
                    <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 animate-pulse" />
                    <span className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                        {lang === "ar" ? "التحقق جارٍ…" : "Verification in Progress"}
                    </span>
                </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-10">
                <h1 className="text-3xl font-bold tracking-tight">
                    {lang === "ar" ? "تم استلام دفعتك!" : "Payment Received!"}
                </h1>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                    {lang === "ar"
                        ? "فريقنا يتحقق من عضويتك. عادةً ما يستغرق هذا من ٢ إلى ١٢ ساعة."
                        : "Our team is verifying your membership. This usually takes 2–12 hours."}
                </p>
                {payRef && (
                    <p className="mt-2 text-sm text-muted-foreground">
                        {lang === "ar" ? `مرجع الدفع: ` : "Payment reference: "}
                        <span className="font-mono font-semibold text-foreground">{payRef}</span>
                    </p>
                )}
            </div>

            {/* Checklist */}
            <div className="rounded-2xl border border-border bg-card p-6 mb-8 space-y-5">
                {checklist.map(({ done, label, sub }, i) => (
                    <div key={i} className="flex items-start gap-4">
                        <div className="shrink-0 mt-0.5">
                            {done
                                ? <CheckCircle2 className="h-5 w-5 text-success" />
                                : i === 1
                                    ? <Clock className="h-5 w-5 text-amber-500 animate-pulse" />
                                    : <Circle className="h-5 w-5 text-muted-foreground/40" />
                            }
                        </div>
                        <div className="min-w-0">
                            <p className={`text-sm font-semibold ${done ? "text-foreground" : "text-muted-foreground"}`}>
                                {label}
                            </p>
                            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
                        </div>
                    </div>
                ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="flex-1 rounded-xl gap-2" size="lg">
                    <Link to="/search">
                        <Search className="h-4 w-4" />
                        {lang === "ar" ? "تصفح مزودي الخدمة" : "Explore Providers"}
                    </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1 rounded-xl gap-2" size="lg">
                    <Link to="/member/card">
                        <CreditCard className="h-4 w-4" />
                        {lang === "ar" ? "عرض البطاقة" : "View My Card"}
                    </Link>
                </Button>
            </div>

            {/* Provider instructions */}
            <div className="mt-8 rounded-xl border border-border/50 bg-muted/30 px-5 py-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    {lang === "ar" ? "تعليمات للمزودين" : "Instructions for Providers"}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                    {lang === "ar"
                        ? 'عند وصولك إلى أي عيادة أو مستشفى، أخبر المستقبِل بأنك "عضو في حماية" وأظهر بطاقتك الرقمية.'
                        : 'When you arrive at a clinic or hospital, tell the receptionist you are a Hemaia Member and show your digital member card.'}
                </p>
            </div>
        </div>
    );
}
